import { useCallback, useEffect } from 'react';
import generateBinauralBeats from '../utils/generateBinauralBeats';
import generateNoise from '../utils/generateNoise';
import audioService from '../services/audioService';

/**
 * Custom hook for managing binaural beats audio playback
 * @param {Object} audioState - Current audio state
 * @param {function} setAudioState - Function to update audio state
 * @param {number} carrierFrequency - Carrier frequency for binaural beats
 * @returns {Object} Audio control functions
 */
export const useAudioPlayer = (audioState, setAudioState, carrierFrequency) => {
  // Retune a playing beat when the carrier frequency changes
  useEffect(() => {
    const { oscillators, beatFrequency } = audioState;
    if (oscillators && carrierFrequency && beatFrequency != null) {
      const currentTime = oscillators.left.context ? oscillators.left.context.currentTime : 0;
      oscillators.left.frequency.setValueAtTime(carrierFrequency, currentTime);
      oscillators.right.frequency.setValueAtTime(carrierFrequency + beatFrequency, currentTime);
    }
  }, [audioState, carrierFrequency]);

  const handleBeatActivate = useCallback((preset) => {
    // If already playing, stop first
    if (audioState.stopFunction) {
      audioState.stopFunction();
      setAudioState(prev => ({
        ...prev,
        stopFunction: null,
        gainNode: null,
        panNodes: null,
        oscillators: null,
        beatFrequency: null,
        activePresetId: null
      }));
    }

    // If clicking the same preset that's already active, just stop
    if (audioState.activePresetId === preset.id) {
      return;
    }

    // Otherwise, start the new preset
    const normalizedPanWidth = audioService.normalizeVolume(audioState.panWidth);
    const initialVolume = audioService.normalizeVolume(audioState.volume);

    const result = generateBinauralBeats(
      carrierFrequency,
      carrierFrequency + preset.frequency,
      (node) => {
        setAudioState((prev) => ({
          ...prev,
          gainNode: node,
        }));
      },
      normalizedPanWidth,
      initialVolume
    );

    setAudioState(prev => ({
      ...prev,
      stopFunction: result.stop,
      panNodes: result.panNodes,
      oscillators: result.oscillators,
      beatFrequency: preset.frequency,
      activePresetId: preset.id
    }));
  }, [audioState, setAudioState, carrierFrequency]);

  const handleNoiseActivate = useCallback((noisePreset) => {
    // If already playing, stop first
    if (audioState.stopFunction) {
      audioState.stopFunction();
      setAudioState(prev => ({
        ...prev,
        stopFunction: null,
        gainNode: null,
        activePresetId: null
      }));
    }

    // If clicking the same noise that's already active, just stop
    if (audioState.activePresetId === noisePreset.id) {
      return;
    }

    // Otherwise, start the new noise
    const normalizedVolume = audioService.normalizeVolume(audioState.volume);

    const result = generateNoise(
      noisePreset.id,
      (node) => {
        setAudioState(prev => ({
          ...prev,
          gainNode: node
        }));
      },
      normalizedVolume
    );

    setAudioState(prev => ({
      ...prev,
      stopFunction: result.stop,
      activePresetId: noisePreset.id
    }));
  }, [audioState, setAudioState]);

  const stopAudio = useCallback(() => {
    if (audioState.stopFunction) {
      audioState.stopFunction();
      setAudioState(prev => ({
        ...prev,
        stopFunction: null,
        gainNode: null,
        panNodes: null,
        oscillators: null,
        beatFrequency: null,
        activePresetId: null
      }));
    }
  }, [audioState, setAudioState]);

  return {
    handleBeatActivate,
    handleNoiseActivate,
    stopAudio
  };
};

export default useAudioPlayer;
